import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';

const styles = `
  .toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }
  .search-input {
    flex: 1;
    max-width: 320px;
    padding: 10px 14px;
    font-family: inherit;
    font-size: 0.9rem;
    border: 1.5px solid #E0DDD8;
    border-radius: 8px;
    background: #fff;
    color: #2C2C2C;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .search-input:focus {
    border-color: #E8845A;
    box-shadow: 0 0 0 3px rgba(232,132,90,0.12);
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: hidden;
  }
  .data-table th {
    text-align: left;
    padding: 12px 16px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #EDE9E4;
    background: #FAFAF8;
  }
  .data-table td {
    padding: 13px 16px;
    font-size: 0.9rem;
    color: #2C2C2C;
    border-bottom: 1px solid #F0EDE8;
  }
  .data-table tr:last-child td { border-bottom: none; }
  .data-table tbody tr {
    cursor: pointer;
    transition: background 0.1s;
  }
  .data-table tbody tr:hover td { background: #FFF8F5; }
  .badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .badge-active { background: #F0FFF4; color: #27AE60; }
  .badge-suspended { background: #FFF0EE; color: #C0392B; }
  .loading-state { color: #888; font-size: 0.9rem; padding: 20px 0; }
  .error-state { color: #C0392B; font-size: 0.9rem; padding: 20px 0; }
`;

function fmt(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.listUsers()
      .then(data => setUsers(data || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout title="Users">
      <style>{styles}</style>

      <div className="toolbar">
        <input
          className="search-input"
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span style={{ color: '#888', fontSize: '0.85rem' }}>
          {filtered.length} user{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {!loading && !error && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Created</th>
              <th>Last Active</th>
              <th>Conversations</th>
              <th>Tokens</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} onClick={() => navigate(`/admin/users/${u.id}`)}>
                <td style={{ fontWeight: 600 }}>{u.name}</td>
                <td style={{ color: '#666' }}>{u.email}</td>
                <td>{fmtDate(u.created_at)}</td>
                <td>{fmtDate(u.last_active)}</td>
                <td>{u.conversation_count}</td>
                <td>{fmt(u.tokens_used)}</td>
                <td>
                  <span className={`badge badge-${u.status}`}>{u.status}</span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: '#888', textAlign: 'center', padding: '24px' }}>
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </AdminLayout>
  );
}
